import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FiBriefcase, FiUsers, FiUser, FiClock, FiTrendingUp, FiChevronRight, FiCalendar,
  FiAlertCircle, FiFileText, FiClipboard, FiDollarSign, FiActivity, FiCheckCircle
} from 'react-icons/fi';
import { getDashboardStats } from '../services/api';
import { DonutChart, TrendBars } from '../components/DashboardCharts';
import DashboardMap from '../components/DashboardMap';
import TodoWidget from '../components/TodoWidget';

// Animated count-up for stat numbers
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    const end = Number(target) || 0;
    let startTs;
    const step = (ts) => {
      if (startTs === undefined) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return value;
}

function VibrantStat({ icon, label, value, gradient, suffix = '', to }) {
  const animated = useCountUp(value);
  const inner = (
    <div className={`dash-stat ${gradient}${to ? ' dash-stat-link' : ''}`}>
      <div className="dash-stat-icon">{icon}</div>
      <div className="dash-stat-value">{animated}{suffix}</div>
      <div className="dash-stat-label">{label}</div>
    </div>
  );
  return to
    ? <Link to={to} style={{ textDecoration: 'none', display: 'contents' }}>{inner}</Link>
    : inner;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Good morning', emoji: '☀️' };
  if (h < 17) return { text: 'Good afternoon', emoji: '🌤️' };
  return { text: 'Good evening', emoji: '🌙' };
}

function Dashboard() {
  const [stats, setStats] = useState({
    total_jobs: 0,
    total_clients: 0,
    active_jobs: 0,
    my_jobs: 0,
    clocked_in_employees: 0,
    jobs_due_today: 0,
    today_hours: 0,
    status_counts: {},
    action_items: { needs_review: 0, needs_invoice: 0, overdue: 0 },
    today_schedule: [],
    completion_trend: [],
    avg_turnaround_days: null,
    workload: [],
    can_view_financials: false,
    financials: null,
    map_jobs: [],
    quote_pipeline: null,
    profitability: null
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Only load stats - recent_jobs is included in the response
      const statsResponse = await getDashboardStats();

      const statsData = statsResponse.data || {};
      setStats({
        total_jobs: statsData.total_jobs || 0,
        total_clients: statsData.total_clients || 0,
        active_jobs: statsData.active_jobs || 0,
        my_jobs: statsData.my_jobs || 0,
        clocked_in_employees: statsData.clocked_in_employees || 0,
        jobs_due_today: statsData.jobs_due_today || 0,
        today_hours: statsData.today_hours || 0,
        status_counts: statsData.status_counts || {},
        action_items: statsData.action_items || { needs_review: 0, needs_invoice: 0, overdue: 0 },
        today_schedule: statsData.today_schedule || [],
        completion_trend: statsData.completion_trend || [],
        avg_turnaround_days: statsData.avg_turnaround_days ?? null,
        workload: statsData.workload || [],
        can_view_financials: statsData.can_view_financials || false,
        financials: statsData.financials || null,
        map_jobs: statsData.map_jobs || [],
        quote_pipeline: statsData.quote_pipeline || null,
        profitability: statsData.profitability || null
      });

      // Use recent_jobs from stats response instead of loading all jobs
      setRecentJobs(statsData.recent_jobs || []);

      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#fbbf24',
      'Scheduled': '#60a5fa',
      'In Progress - Initial Field Work': '#a78bfa',
      'In Progress - Additional Field Work Required': '#7c3aed',
      'In Progress - Drafting': '#8b5cf6',
      'Survey Complete': '#34d399',
      'Needs Review': '#f97316',
      'Final Mark Up / Stake Lines': '#ec4899',
      'Needs Invoice': '#eab308',
      'Invoiced': '#fb923c',
      'Paid': '#10b981'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        color: '#666'
      }}>
        Loading dashboard...
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{ padding: '5px' }}>
        {/* Stats Grid - 2x2 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <StatCard 
            icon={<FiBriefcase />} 
            label="Total Jobs" 
            value={stats.total_jobs}
            color="#2563eb"
            bgColor="#dbeafe"
          />
          <StatCard 
            icon={<FiClock />} 
            label="Active" 
            value={stats.active_jobs}
            color="#d97706"
            bgColor="#fef3c7"
          />
          <StatCard 
            icon={<FiUsers />} 
            label="Clocked In" 
            value={stats.clocked_in_employees}
            color="#059669"
            bgColor="#d1fae5"
          />
          <StatCard
            icon={<FiTrendingUp />}
            label="Clients"
            value={stats.total_clients}
            color="#7c3aed"
            bgColor="#e9d5ff"
          />
        </div>

        {/* My To-Do */}
        <div style={{ marginBottom: '20px' }}>
          <TodoWidget />
        </div>

        {/* Recent Jobs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Recent Jobs</h2>
            <Link to="/jobs" style={{ 
              color: '#2563eb', 
              textDecoration: 'none',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              View All <FiChevronRight size={16} />
            </Link>
          </div>

          {recentJobs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {recentJobs.map(job => (
                <Link 
                  key={job.id} 
                  to={`/jobs/${job.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    color: 'inherit',
                    minHeight: '44px'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '15px',
                      color: '#333',
                      marginBottom: '4px'
                    }}>
                      {job.job_number}
                    </div>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120px'
                      }}>
                        {job.client_name}
                      </div>
                      {job.client_phone && (
                        <a
                          href={`tel:${job.client_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: '#2563eb',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                        >
                          📞
                        </a>
                      )}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: getStatusColor(job.status),
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    marginLeft: '10px',
                    whiteSpace: 'nowrap'
                  }}>
                    {job.status.replace('In Progress - ', '')}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '30px',
              color: '#999'
            }}>
              <FiBriefcase size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
              <p style={{ margin: 0 }}>No jobs yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout
  // Exclude non-workflow buckets (Imported, Cancelled) from the status chart
  const HIDDEN_STATUSES = ['Imported', 'Cancelled'];
  const statusCountsArray = Object.entries(stats.status_counts || {})
    .filter(([status]) => !HIDDEN_STATUSES.includes(status));
  const statusChartTotal = statusCountsArray.reduce((sum, [, count]) => sum + count, 0);
  const paidCount = stats.status_counts?.['Paid'] || 0;
  const importedCount = stats.status_counts?.['Imported'] || 0;
  const cancelledCount = stats.status_counts?.['Cancelled'] || 0;
  const activeTotal = stats.total_jobs - importedCount - cancelledCount;
  const completionRate = activeTotal > 0
    ? Math.round((paidCount / activeTotal) * 100)
    : 0;

  const greeting = getGreeting();
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric'
  });

  const fmtMoney = (n) => '$' + (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const ai = stats.action_items || {};
  const attentionCards = [
    { key: 'overdue', label: 'Overdue Jobs', value: ai.overdue || 0, icon: <FiAlertCircle />, color: '#ef4444', to: '/jobs?overdue=true' },
    { key: 'needs_review', label: 'Needs Review', value: ai.needs_review || 0, icon: <FiClipboard />, color: '#f97316', to: `/jobs?status=${encodeURIComponent('Needs Review')}` },
    { key: 'needs_invoice', label: 'Needs Invoice', value: ai.needs_invoice || 0, icon: <FiFileText />, color: '#eab308', to: `/jobs?status=${encodeURIComponent('Needs Invoice')}` },
  ];
  if (stats.can_view_financials && stats.profitability) {
    attentionCards.push({
      key: 'over_budget', label: 'Over Budget', value: stats.profitability.jobs_over_budget || 0,
      icon: <FiDollarSign />, color: '#dc2626', to: '/jobs?over_budget=true'
    });
  }

  const mapJobs = (stats.map_jobs || []).map(j => ({ ...j, color: getStatusColor(j.status) }));
  const qp = stats.quote_pipeline;
  const prof = stats.profitability;

  const donutData = statusCountsArray.map(([status, count]) => ({
    label: status, value: count, color: getStatusColor(status)
  }));

  const trendData = (stats.completion_trend || []).map(m => ({ label: m.label, value: m.count }));
  const trendTotal = trendData.reduce((s, m) => s + m.value, 0);

  const maxWorkload = Math.max(1, ...(stats.workload || []).map(w => w.count));

  return (
    <div className="dashboard-page">
      {/* Vibrant hero header */}
      <div className="dash-hero">
        <h1>{greeting.text} {greeting.emoji}</h1>
        <p>{today} · {stats.active_jobs} active jobs · {stats.clocked_in_employees} on the clock</p>
      </div>

      {/* Vibrant Stat Cards */}
      <div className="dash-stats">
        <VibrantStat
          icon={<FiUser />}
          label="My Jobs"
          value={stats.my_jobs}
          gradient="dash-grad-indigo"
          to="/jobs?mine=true"
        />
        <VibrantStat
          icon={<FiBriefcase />}
          label="Total Jobs"
          value={stats.total_jobs}
          gradient="dash-grad-blue"
        />
        <VibrantStat
          icon={<FiClock />}
          label="Active Jobs"
          value={stats.active_jobs}
          gradient="dash-grad-amber"
        />
        <VibrantStat
          icon={<FiCalendar />}
          label="Due Today"
          value={stats.jobs_due_today}
          gradient="dash-grad-pink"
        />
        <VibrantStat
          icon={<FiUsers />}
          label="Clocked In"
          value={stats.clocked_in_employees}
          gradient="dash-grad-green"
        />
        <VibrantStat
          icon={<FiTrendingUp />}
          label="Completion Rate"
          value={completionRate}
          gradient="dash-grad-purple"
          suffix="%"
        />
      </div>

      {/* Needs Attention */}
      <div className="dash-attention">
        {attentionCards.map(card => (
          <Link
            key={card.key}
            to={card.to}
            className={`dash-attention-card${card.value === 0 ? ' is-clear' : ''}`}
            style={{ '--accent': card.value === 0 ? '#9ca3af' : card.color }}
          >
            <div className="dash-att-icon" style={{ background: card.value === 0 ? '#9ca3af' : card.color }}>
              {card.value === 0 ? <FiCheckCircle /> : card.icon}
            </div>
            <div>
              <div className="dash-att-num">{card.value}</div>
              <div className="dash-att-label">{card.label}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Job Map (Qfactor-inspired: map front and center) */}
      {mapJobs.length > 0 && (
        <div className="dash-panel" style={{ marginBottom: '1.5rem' }}>
          <div className="dash-panel-header">
            <h2>📍 Active Jobs Map</h2>
            <Link to="/map" className="dash-panel-meta" style={{ textDecoration: 'none' }}>Full map →</Link>
          </div>
          <DashboardMap jobs={mapJobs} height={340} />
        </div>
      )}

      {/* Financials (payroll managers only) */}
      {stats.can_view_financials && stats.financials && (
        <div className="dash-panel" style={{ marginBottom: '1.5rem' }}>
          <div className="dash-panel-header">
            <h2><FiDollarSign style={{ verticalAlign: '-2px', marginRight: 6 }} />Financial Snapshot</h2>
          </div>
          <div className="dash-fin-grid">
            <div className="dash-fin-card">
              <div className="dash-fin-value">{fmtMoney(stats.financials.outstanding)}</div>
              <div className="dash-fin-label">Outstanding (Invoiced)</div>
            </div>
            <div className="dash-fin-card">
              <div className="dash-fin-value" style={{ color: '#059669' }}>{fmtMoney(stats.financials.paid_this_month)}</div>
              <div className="dash-fin-label">Paid This Month</div>
            </div>
            <div className="dash-fin-card">
              <div className="dash-fin-value" style={{ color: (stats.financials.overdue_ar > 0) ? '#ef4444' : undefined }}>{fmtMoney(stats.financials.overdue_ar)}</div>
              <div className="dash-fin-label">Overdue A/R</div>
            </div>
            <div className="dash-fin-card">
              <div className="dash-fin-value">
                {stats.financials.avg_turnaround_days != null ? `${stats.financials.avg_turnaround_days}` : '—'}
                {stats.financials.avg_turnaround_days != null && <span style={{ fontSize: '0.9rem', fontWeight: 600 }}> days</span>}
              </div>
              <div className="dash-fin-label">Median Turnaround</div>
            </div>
          </div>
        </div>
      )}

      {/* Quote / Proposal pipeline (payroll managers only) */}
      {stats.can_view_financials && qp && (
        <div className="dash-panel" style={{ marginBottom: '1.5rem' }}>
          <div className="dash-panel-header">
            <h2><FiFileText style={{ verticalAlign: '-2px', marginRight: 6 }} />Quote Pipeline</h2>
            <Link to="/billing" className="dash-panel-meta" style={{ textDecoration: 'none' }}>Manage quotes →</Link>
          </div>
          <div className="dash-fin-grid">
            <div className="dash-fin-card">
              <div className="dash-fin-value">{fmtMoney(qp.open_value)}</div>
              <div className="dash-fin-label">Open Quotes ({qp.open_count})</div>
            </div>
            <div className="dash-fin-card">
              <div className="dash-fin-value" style={{ color: '#059669' }}>{fmtMoney(qp.approved_value)}</div>
              <div className="dash-fin-label">Approved, Pending ({qp.approved_count})</div>
            </div>
            <div className="dash-fin-card">
              <div className="dash-fin-value">{fmtMoney((qp.open_value || 0) + (qp.approved_value || 0))}</div>
              <div className="dash-fin-label">Total Pipeline Value</div>
            </div>
          </div>
        </div>
      )}

      {/* Two-column widget grid */}
      <div className="dash-grid">
        {/* Completion trend */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h2><FiActivity style={{ verticalAlign: '-2px', marginRight: 6 }} />Jobs Completed</h2>
            <span className="dash-panel-meta">{trendTotal} in last 6 months</span>
          </div>
          {trendTotal > 0 ? (
            <TrendBars data={trendData} color="#6366f1" />
          ) : (
            <div className="dash-mini-empty">No completed jobs in this window yet</div>
          )}
        </div>

        {/* Status donut */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h2>Jobs by Status</h2>
            <span className="dash-panel-meta">{statusChartTotal} active</span>
          </div>
          {donutData.length > 0 ? (
            <div className="dash-donut-wrap">
              <DonutChart data={donutData} centerValue={statusChartTotal} centerLabel="Jobs" />
              <div className="dash-legend">
                {donutData.map(d => (
                  <div key={d.label} className="dash-legend-row">
                    <span className="dash-legend-dot" style={{ background: d.color }} />
                    <span className="dash-legend-label">{d.label}</span>
                    <span className="dash-legend-count">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dash-mini-empty">No jobs to chart</div>
          )}
        </div>

        {/* Job profitability (payroll managers only) */}
        {stats.can_view_financials && prof && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <h2><FiDollarSign style={{ verticalAlign: '-2px', marginRight: 6 }} />Job Profitability</h2>
              <span className="dash-panel-meta">{prof.jobs_over_budget} over budget</span>
            </div>
            {prof.jobs && prof.jobs.length > 0 ? (
              <table className="dash-prof-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th style={{ textAlign: 'right' }}>Revenue</th>
                    <th style={{ textAlign: 'right' }}>Labor</th>
                    <th style={{ textAlign: 'right' }}>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {prof.jobs.map(j => (
                    <tr key={j.id} className={j.over_budget ? 'is-over' : ''}>
                      <td>
                        <Link to={`/jobs/${j.id}`} className="dash-prof-job">{j.job_number}</Link>
                      </td>
                      <td style={{ textAlign: 'right' }}>{j.revenue ? fmtMoney(j.revenue) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(j.labor_cost)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: j.margin == null ? '#9ca3af' : (j.margin < 0 ? '#dc2626' : '#059669') }}>
                        {j.margin == null ? '—' : fmtMoney(j.margin)}
                        {j.margin_pct != null && <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af' }}> ({j.margin_pct}%)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="dash-mini-empty">No labor logged on active jobs yet</div>
            )}
            <div className="dash-prof-foot">Labor cost = logged hours × pay rate. Revenue = contract or invoiced amount.</div>
          </div>
        )}

        {/* Today's schedule */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h2><FiCalendar style={{ verticalAlign: '-2px', marginRight: 6 }} />Today's Schedule</h2>
            <Link to="/schedule" className="dash-panel-meta" style={{ textDecoration: 'none' }}>View all →</Link>
          </div>
          {stats.today_schedule.length > 0 ? (
            <div>
              {stats.today_schedule.map(job => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="dash-schedule-row">
                  <div className="dash-schedule-time">{job.scheduled_time || '—'}</div>
                  <div className="dash-schedule-main">
                    <div className="dash-sch-job">{job.job_number}</div>
                    <div className="dash-sch-client">{job.client_name || 'No client'}</div>
                  </div>
                  {job.crew && job.crew.length > 0 && (
                    <div className="dash-schedule-crew">{job.crew.join(', ')}</div>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="dash-mini-empty">Nothing scheduled for today 🎉</div>
          )}
        </div>

        {/* Workload by surveyor */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h2><FiUsers style={{ verticalAlign: '-2px', marginRight: 6 }} />Active Workload</h2>
            <span className="dash-panel-meta">by team member</span>
          </div>
          {stats.workload.length > 0 ? (
            <div>
              {stats.workload.map(w => (
                <div key={w.name} className="dash-workload-row">
                  <div className="dash-workload-top">
                    <span className="dash-workload-name">{w.name}</span>
                    <span className="dash-workload-count">{w.count} {w.count === 1 ? 'job' : 'jobs'}</span>
                  </div>
                  <div className="dash-workload-track">
                    <div className="dash-workload-fill" style={{ width: `${(w.count / maxWorkload) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dash-mini-empty">No active assignments</div>
          )}
        </div>
      </div>

      {/* My To-Do */}
      <div className="dashboard-section" style={{ marginBottom: '20px' }}>
        <TodoWidget />
      </div>

      {/* Recent Jobs */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Recent Jobs</h2>
          <Link to="/jobs" className="btn btn-secondary">View All</Link>
        </div>
        
        {recentJobs.length > 0 ? (
          <div className="jobs-list">
            {recentJobs.map(job => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="job-card">
                <div className="job-header">
                  <h3>{job.job_number}</h3>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(job.status) }}
                  >
                    {job.status}
                  </span>
                </div>
                <p className="job-client">{job.client_name}</p>
                {(job.client_phone || job.client_email) && (
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>
                    {job.client_phone && (
                      <a 
                        href={`tel:${job.client_phone}`}
                        style={{ color: '#2563eb', marginRight: '12px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        📞 {job.client_phone}
                      </a>
                    )}
                    {job.client_email && (
                      <a 
                        href={`mailto:${job.client_email}`}
                        style={{ color: '#2563eb' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        ✉️ Email
                      </a>
                    )}
                  </div>
                )}
                {job.property_address && (
                  <p className="job-address">{job.property_address}</p>
                )}
                {job.scheduled_date && (
                  <p className="job-date">
                    Scheduled: {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()}
                  </p>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No jobs yet</p>
            <Link to="/jobs" className="btn btn-primary">Create Your First Job</Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <Link to="/clients" className="action-card">
            <FiUsers />
            <h3>Manage Clients</h3>
            <p>Add and view clients</p>
          </Link>
          
          <Link to="/jobs" className="action-card">
            <FiBriefcase />
            <h3>Manage Jobs</h3>
            <p>Create and track jobs</p>
          </Link>
          
          <Link to="/calendar" className="action-card">
            <FiClock />
            <h3>View Calendar</h3>
            <p>See scheduled work</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Mobile Stat Card Component
function StatCard({ icon, label, value, color, bgColor }) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        marginBottom: '10px',
        fontSize: '20px'
      }}>
        {icon}
      </div>
      <div style={{ 
        fontSize: '24px', 
        fontWeight: '700',
        color: '#333',
        lineHeight: 1
      }}>
        {value}
      </div>
      <div style={{ 
        fontSize: '13px', 
        color: '#666',
        marginTop: '4px'
      }}>
        {label}
      </div>
    </div>
  );
}

export default Dashboard;
